import { FC, useState, useCallback } from 'react';
import isEqual from 'lodash-es/isEqual';
import { useQueryClient } from 'react-query';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Button, PopoverDialog, usePopoverDialog } from '@frontend/design-system';
import { DEFAULT_FILTER_VALUES } from '../../constants';
import { useTaskCenterStore } from '../../provider';
import { SelectedFilters } from '../../types';
import { popoverStyle, applyButtonStyle } from './task-filters.styles';
import { TaskLocationFilter } from './task-location-filter';
import { TaskPersonFilter } from './task-person-filter';
import { TaskPopoverHeading } from './task-popover-heading';
import { TaskSortOrder } from './task-sort-order';
import { TaskSourceFilter } from './task-source-filter';

export const TaskFilters: FC = () => {
  const {
    selectedFilters,
    setFilters: setStoreFilters,
    locationId,
  } = useTaskCenterStore(['selectedFilters', 'setFilters', 'locationId']);
  const [filters, setFilters] = useState<SelectedFilters>({ ...selectedFilters });
  const { t } = useTranslation('task-center');
  const popoverDialogProps = usePopoverDialog({});
  const queryClient = useQueryClient();

  const disableFiltersClear = !filters.createdBy && !filters.source;

  // Filters Clear functions
  const onClearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTER_VALUES, sortType: filters.sortType, locations: locationId });
  }, [setFilters]);
  const onClearSort = useCallback(() => {
    setFilters({ ...selectedFilters, sortType: DEFAULT_FILTER_VALUES.sortType });
  }, [setFilters]);

  // Filters data update functions
  const setLocations = useCallback((locations: string[]) => setFilters({ ...filters, locations }), [filters]);
  const setCreatedBy = useCallback((createdBy: string) => setFilters({ ...filters, createdBy }), [filters]);
  const setSource = useCallback((source: TaskCenterTypes.TaskSource) => setFilters({ ...filters, source }), [filters]);
  const setSortOrder = useCallback(
    (sortType: TaskCenterTypes.SortField | null) => setFilters({ ...filters, sortType }),
    [filters]
  );

  // Apply button functions
  const disableApply = isEqual(filters, selectedFilters);
  const onClickApply = useCallback(() => {
    setStoreFilters(filters);
    queryClient.invalidateQueries('task-center');
    popoverDialogProps.close();
  }, [filters, setStoreFilters]);

  return (
    <span>
      <Button
        {...popoverDialogProps.getTriggerProps()}
        variant='secondary'
        iconName='filter-alt'
        size='large'
        hoverLabel={t('Filters')}
      />
      <PopoverDialog {...popoverDialogProps.getDialogProps()} css={popoverStyle}>
        <TaskPopoverHeading label={t('Sort Order')} onClear={onClearSort} disableClear={!filters.sortType} />
        <TaskSortOrder sortOrder={filters.sortType} setSortOrder={setSortOrder} />
        <TaskPopoverHeading label={t('Other Filters')} onClear={onClearFilters} disableClear={disableFiltersClear} />
        <TaskPersonFilter selectedValue={filters.createdBy} setValue={setCreatedBy} label={t('Created By')} />
        <TaskLocationFilter selectedLocations={filters.locations} setSelectedLocations={setLocations} />
        <TaskSourceFilter source={filters.source} setSource={setSource} />
        <Button onClick={onClickApply} disabled={disableApply} css={applyButtonStyle}>
          {t('Apply')}
        </Button>
      </PopoverDialog>
    </span>
  );
};
